import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KantoorSidebar } from "@/components/KantoorSidebar";
import { MobileBottomTabs } from "@/components/MobileBottomTabs";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { Logo } from "@/components/Logo";
import { Save, Building2 } from "lucide-react";

const ALL_SPECIALISMES = [
  "Boekhouding uitbesteden", "Btw-aangifte", "Jaarrekening opstellen", "Belastingadvies",
  "Salarisadministratie", "Bedrijfsadvies", "Administratie", "Accountancy",
  "Procesoptimalisatie", "Ondernemerschap",
];

const KantoorProfiel = () => {
  const { t, i18n } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [kantoor, setKantoor] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    api.get("/kantoor/me").then((r) => setKantoor(r.data)).finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  if (loading || authLoading || !kantoor) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#F5A623] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const set = (k, v) => setKantoor((p) => ({ ...p, [k]: v }));
  const toggleSpec = (s) => {
    setKantoor((p) => ({
      ...p,
      specialismes: p.specialismes.includes(s) ? p.specialismes.filter((x) => x !== s) : [...p.specialismes, s],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch("/kantoor/me", {
        naam: kantoor.naam,
        beschrijving: kantoor.beschrijving,
        specialismes: kantoor.specialismes,
        locatie: kantoor.locatie,
        logo_url: kantoor.logo_url,
      });
      setKantoor(res.data);
      setSavedAt(new Date());
    } catch { alert(t("kantoor_profiel.save_failed")); } finally { setSaving(false); }
  };

  const lang = i18n.language?.startsWith("en") ? "en-US" : "nl-NL";

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]">
      <KantoorSidebar />
      <main className="flex-1 min-w-0">
        <div className="lg:hidden bg-white border-b border-slate-200 px-4 h-14 flex items-center"><Logo size="sm" /></div>
        <div className="p-4 md:p-8 pb-24 lg:pb-8 max-w-3xl mx-auto">
          <h1 className="font-heading text-3xl font-bold text-[#1A2B4A]">{t("kantoor_profiel.title")}</h1>
          <p className="text-slate-600 mt-1">{t("kantoor_profiel.subtitle")}</p>

          <div className="mt-8 beecenter-card p-6">
            <div className="flex items-start gap-5">
              <div className="w-20 h-20 rounded-2xl overflow-hidden bg-slate-100 ring-1 ring-slate-200 flex-shrink-0">
                {kantoor.logo_url ? (
                  <img src={kantoor.logo_url} alt={kantoor.naam} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Building2 className="text-slate-400" /></div>
                )}
              </div>
              <div className="flex-1">
                <label className="text-sm font-semibold text-[#1A2B4A]">{t("kantoor_profiel.logo_url")}</label>
                <input value={kantoor.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)}
                  placeholder="https://…" className="mt-1.5 w-full rounded-lg border border-slate-300 px-4 py-2 outline-none focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20"
                  data-testid="input-logo-url" />
              </div>
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-[#1A2B4A]">{t("kantoor_profiel.name")}</label>
                <input value={kantoor.naam} onChange={(e) => set("naam", e.target.value)}
                  className="mt-1.5 w-full rounded-lg border border-slate-300 px-4 py-2 outline-none focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20"
                  data-testid="input-kantoor-naam" />
              </div>
              <div>
                <label className="text-sm font-semibold text-[#1A2B4A]">{t("kantoor_profiel.locatie")}</label>
                <input value={kantoor.locatie} onChange={(e) => set("locatie", e.target.value)}
                  className="mt-1.5 w-full rounded-lg border border-slate-300 px-4 py-2 outline-none focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20"
                  data-testid="input-kantoor-locatie" />
              </div>
            </div>

            <div className="mt-6">
              <label className="text-sm font-semibold text-[#1A2B4A]">{t("kantoor_profiel.beschrijving")}</label>
              <textarea value={kantoor.beschrijving} onChange={(e) => set("beschrijving", e.target.value)}
                rows={4} className="mt-1.5 w-full rounded-lg border border-slate-300 px-4 py-3 outline-none focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20 resize-none"
                data-testid="input-beschrijving" />
            </div>

            <div className="mt-6">
              <label className="text-sm font-semibold text-[#1A2B4A]">{t("kantoor_profiel.specialismes")}</label>
              <div className="mt-3 flex flex-wrap gap-2">
                {ALL_SPECIALISMES.map((s) => {
                  const active = kantoor.specialismes.includes(s);
                  return (
                    <button key={s} onClick={() => toggleSpec(s)} type="button"
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-all ${
                        active ? "border-[#F5A623] bg-[#F5A623] text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                      }`} data-testid={`spec-toggle-${s}`}>
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-7 flex items-center justify-between flex-wrap gap-3">
              <div className="text-xs text-slate-500">
                {savedAt
                  ? t("kantoor_profiel.saved_at", { time: savedAt.toLocaleTimeString(lang, { hour: "2-digit", minute: "2-digit" }) })
                  : t("kantoor_profiel.not_saved")}
              </div>
              <button onClick={save} disabled={saving} className="btn-amber !py-2.5 disabled:opacity-50" data-testid="btn-save-profile">
                <Save size={16} /> {saving ? t("common.loading") : t("kantoor_profiel.save")}
              </button>
            </div>
          </div>
        </div>
        <MobileBottomTabs />
      </main>
    </div>
  );
};

export default KantoorProfiel;
